import { Tabs } from 'antd'
import { TabsProps } from 'antd/lib/tabs'
import { FunctionComponent, useState } from 'react'
import styled from 'styled-components'
import { Icon } from '@/components/custom-icon'
import useKolDetailRoute from '@/components/kol/hooks/use-kol-detail-route'
import {
  TabKey,
  TabOption,
  tabSchema,
} from '@/components/kol/hooks/use-section-tabs'
import useIsPublicDetailPage from '@/hooks/use-is-public-detail-page'
import { useIntl } from '@/i18n/hooks/use-intl'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import {
  ampli,
  ChooseKolDetailSectionFromPublicProperties,
  ClickSideNavigatorProperties,
} from '@/utils/ampli'
import logger from '@/utils/logger'
import { toVoidFunction } from '@/utils/to-void-function'

export const KOL_DETAIL_SECTION_TABS_HEIGHT = 50

interface SectionTabsProps {
  activeTab: TabKey
  kolInfo?: KolSchemaModel
  tabOptions: TabOption[]
  tabBarExtraContent?: TabsProps['tabBarExtraContent']
  onChange: (value: TabKey) => void
}

const kolMetricsMap: Record<
  TabKey,
  ClickSideNavigatorProperties['kolMetrics']
> = {
  overview: 'Overview',
  audienceAnalyses: 'Audience',
  cooperationAndValue: 'Collaboration and Value',
  similarKols: 'Similar influencers',
  engagementAnalyses: 'Engagements',
  creativeContentAnalyses: 'Contents',
}

const kolSectionMap: Record<
  TabKey,
  ChooseKolDetailSectionFromPublicProperties['kolSection']
> = {
  overview: 'Overview',
  audienceAnalyses: 'Audience',
  cooperationAndValue: 'Collaboration and Value',
  similarKols: 'Similar influencers',
  engagementAnalyses: 'Engagements',
  creativeContentAnalyses: 'Content',
}

const SectionTabs: FunctionComponent<SectionTabsProps> = ({
  activeTab,
  kolInfo,
  tabOptions,
  onChange,
  tabBarExtraContent,
}) => {
  const { formatMessage } = useIntl()
  const isPublicKolDetailPage = useIsPublicDetailPage()
  const { searchParams, setSearchParams } = useKolDetailRoute()
  const [isProcessing, setIsProcessing] = useState(false)

  const handleOnChange = async (key: TabKey): Promise<void> => {
    if (isProcessing) {
      return
    }

    setIsProcessing(true)

    try {
      const tabKey = tabSchema.parse(key)
      onChange(tabKey)

      if (isPublicKolDetailPage) {
        ampli.chooseKolDetailSectionFromPublic({
          kolId: kolInfo?.id,
          kolCategory: kolInfo?.customizedTagsNames,
          kolName: kolInfo?.name,
          kolSection: kolSectionMap[tabKey],
        })
      } else {
        ampli.clickSideNavigator({
          kolId: kolInfo?.id,
          kolMetrics: kolMetricsMap[tabKey],
        })
      }

      await setSearchParams(searchParams.data, tabKey)
    } catch (error) {
      logger.error(error)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <TabsWrapper>
      <Tabs
        activeKey={activeTab}
        animated={false}
        items={tabOptions.map((tabOption) => {
          return {
            label: formatMessage({ id: tabOption.i18n }),
            key: tabOption.key,
            disabled: isProcessing,
          }
        })}
        moreIcon={<Icon type='more-horizontal' />}
        tabBarExtraContent={tabBarExtraContent}
        tabBarGutter={0}
        onChange={toVoidFunction(handleOnChange)}
      />
    </TabsWrapper>
  )
}

const TabsWrapper = styled.div`
  .ant-tabs {
    padding: 0 40px;
    margin: 0 auto;
    max-width: 1280px;
  }

  .ant-tabs-top > .ant-tabs-nav {
    margin: 0;

    ::before {
      border: none;
    }
  }

  .ant-tabs-top > .ant-tabs-nav .ant-tabs-ink-bar {
    height: 3px;
  }

  .ant-tabs-tab {
    height: ${KOL_DETAIL_SECTION_TABS_HEIGHT}px;
    font-size: 15px;
    font-style: normal;
    font-weight: 600;

    :hover {
      color: ${({ theme }): string => theme.colors.brand.primary};
    }
  }

  .ant-tabs-tab-active .ant-anchor-link-title {
    color: ${({ theme }): string => theme.colors.brand.primary};
  }
`

export default SectionTabs
